/**
 * Media client - the host site's media library. Unlike the rest of web_crm it
 * sends `X-Site-Id`, because the media routes are site-scoped rather than
 * platform-scoped; siteId is the host site's id (`hostSiteId(user)`). The
 * permission is `manage:media` (a superadmin passes regardless).
 *
 * ⚠ Images are served from `/uploads/...` when R2 is off - a RELATIVE path
 * (`publicUrl` in apps/api/src/lib/storage.ts), which the vite dev server
 * proxies to the API. Fine on a page, dead anywhere the page is not: anything
 * that puts one of these URLs into a letter, or on the clipboard for someone to
 * paste elsewhere, has to make it absolute first - `absoluteMediaUrl` below.
 */
export interface MediaAsset {
  id: string;
  url: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  folder: string;
}

export interface MediaPage {
  items: MediaAsset[];
  total: number;
  page: number;
  pages: number;
}

function siteHeaders(siteId: string): Record<string, string> {
  return { "X-Site-Id": siteId };
}

async function jsonFetch<T>(path: string, siteId: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: { ...siteHeaders(siteId), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

export function listMedia(
  siteId: string,
  params: { folder?: string; search?: string; mime?: string; page?: number; limit?: number } = {},
): Promise<MediaPage> {
  const qs = new URLSearchParams({ page: String(params.page ?? 1), limit: String(params.limit ?? 48) });
  if (params.search) qs.set("search", params.search);
  if (params.folder) qs.set("folder", params.folder);
  if (params.mime) qs.set("mime", params.mime);
  return jsonFetch<MediaPage>(`/admin/media?${qs}`, siteId);
}

/**
 * A media URL that still resolves once it leaves this page.
 *
 * The library hands back `/uploads/<key>` whenever R2 is off (`publicUrl` in
 * apps/api/src/lib/storage.ts) - fine on a page served from the same host, a
 * dead image in somebody's mail client and a dead link on somebody's clipboard.
 * Resolving it against the current origin is a guess about where media is
 * served from, but it is a much better guess than a path with no host at all.
 *
 * Anything already absolute (or protocol-relative) is left alone. It lives here
 * rather than beside a caller because the reason it exists is the storage rule
 * documented at the top of this file, and two copies of that rule would drift.
 */
export function absoluteMediaUrl(url: string): string {
  if (!url || /^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith("//")) return url;
  if (typeof window === "undefined") return url;
  return new URL(url, window.location.origin).href;
}

/** Images only - used by the logo picker. */
export const listImages = (siteId: string, params: { search?: string; page?: number; limit?: number } = {}) =>
  listMedia(siteId, { ...params, mime: "image/" });

export function listFolders(siteId: string): Promise<string[]> {
  return jsonFetch<string[]>("/admin/media/folders", siteId);
}

export function createFolder(siteId: string, path: string): Promise<unknown> {
  return jsonFetch("/admin/media/folders", siteId, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path }),
  });
}

export function deleteAsset(siteId: string, id: string): Promise<unknown> {
  return jsonFetch(`/admin/media/${id}`, siteId, { method: "DELETE" });
}

export async function uploadFile(siteId: string, file: File, folder = "/"): Promise<MediaAsset> {
  const form = new FormData();
  form.append("file", file);
  form.append("folder", folder);
  const res = await fetch("/api/admin/media", {
    method: "POST",
    credentials: "include",
    headers: siteHeaders(siteId),
    body: form,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<MediaAsset>;
}

/** Backwards-compatible alias kept for the logo picker. */
export const uploadImage = (siteId: string, file: File) => uploadFile(siteId, file, "/");
